import type {
  ActiveContextHydration,
  ActiveOrg,
  ActiveUserOption,
  OrganizationSelection,
} from "@/schema";
import fetchClient from "./axiosInstance";
import { readSessionSnapshot, replaceSessionSlot } from "./sessionSnapshot";

// LEGACY localStorage key — migrated INTO the one atomic session snapshot
// (sweep §4). Read once for migration, removed on sight; never written again.
const LEGACY_ACTIVE_CONTEXT_STORAGE_KEY = "causit_active_context";
const LIST_CACHE_MS = 10 * 60 * 1000;

export type ActiveUserSummary = ActiveUserOption;

export interface OrgListItem extends ActiveOrg {
  managed?: boolean;
  inherited?: boolean;
}

export interface ActiveContext {
  fingerprint: string | null;
  user: ActiveUserSummary;
  org: ActiveOrg;
  options: {
    users: ScopedOptions<ActiveUserSummary>;
    orgs: ScopedOptions<OrgListItem>;
  };
  pendingTenant?: PendingTenantAttachment;
}

interface PendingTenantAttachment {
  userId: string;
  parentOrgId: string;
  child: Pick<ActiveOrg, "id" | "name">;
}

interface ScopedOptions<T> {
  ownerId: string;
  items: T[];
  hydratedAt: number;
}

interface ListResponse<T> {
  actor?: string;
  items?: T[];
}

interface AttachTenantResponse {
  userId: string;
  parentOrgId: string;
  tenant: OrgListItem;
  items: OrgListItem[];
}

const normalizedUser = (user: ActiveUserSummary): ActiveUserSummary => {
  const id = String(user.id || "").trim();
  if (!id) throw new Error("Active user id is required");
  return {
    id,
    ...(user.type !== undefined ? { type: user.type } : {}),
    ...(user.activeOrgId !== undefined ? { activeOrgId: user.activeOrgId } : {}),
    ...(user.name ? { name: user.name } : {}),
    ...(user.fullName ? { fullName: user.fullName } : {}),
    ...(user.firstName ? { firstName: user.firstName } : {}),
    ...(user.lastName ? { lastName: user.lastName } : {}),
    ...(user.email ? { email: user.email } : {}),
  };
};

const normalizedOrg = (org: ActiveOrg): ActiveOrg => {
  const id = String(org.id || "").trim();
  const name = String(org.name || "").trim();
  if (!id || !name) {
    throw new Error("Active organization requires an id and hydrated name");
  }
  return {
    id,
    name,
    ...(org.parentOrgId !== undefined ? { parentOrgId: org.parentOrgId } : {}),
    ...(org.plan !== undefined ? { plan: org.plan } : {}),
    ...(org.orgRole !== undefined ? { orgRole: org.orgRole } : {}),
    ...(org.scopes !== undefined ? { scopes: org.scopes } : {}),
    ...(org.managed !== undefined ? { managed: org.managed } : {}),
    ...(org.inherited !== undefined ? { inherited: org.inherited } : {}),
  };
};

class ActiveContextStore {
  private userListInFlight: Promise<ActiveUserSummary[]> | null = null;
  private orgListInFlight: Promise<OrgListItem[]> | null = null;
  private tenantAttachmentInFlight: Promise<OrgListItem | null> | null = null;

  private emptyOptions<T>(ownerId: string): ScopedOptions<T> {
    return { ownerId, items: [], hydratedAt: 0 };
  }

  /** The active context lives in the ONE session snapshot; a legacy
   * `causit_active_context` localStorage record migrates in atomically on
   * first read and the legacy key is removed (the replace-whole-snapshot law
   * holds — migration is one slot replace). */
  private rawContext(): unknown {
    const held = readSessionSnapshot().activeContext;
    if (held) return held;
    try {
      const legacy = localStorage.getItem(LEGACY_ACTIVE_CONTEXT_STORAGE_KEY);
      if (!legacy) return null;
      const parsed = JSON.parse(legacy) as unknown;
      replaceSessionSlot("activeContext", parsed);
      localStorage.removeItem(LEGACY_ACTIVE_CONTEXT_STORAGE_KEY);
      return parsed;
    } catch (error) {
      localStorage.removeItem(LEGACY_ACTIVE_CONTEXT_STORAGE_KEY);
      if (error instanceof SyntaxError) {
        throw new Error("Active context storage is invalid");
      }
      throw error;
    }
  }

  public read(): ActiveContext | null {
    try {
      const raw = this.rawContext();
      if (!raw) return null;
      const parsed = raw as ActiveContext;
      const pendingTenant = parsed.pendingTenant;
      return {
        fingerprint: typeof parsed.fingerprint === "string" && parsed.fingerprint
          ? parsed.fingerprint
          : null,
        user: normalizedUser(parsed.user),
        org: normalizedOrg(parsed.org),
        options: {
          users: {
            ownerId: String(parsed.options?.users?.ownerId || ""),
            items: Array.isArray(parsed.options?.users?.items)
              ? parsed.options.users.items.map(normalizedUser)
              : [],
            hydratedAt: Number(parsed.options?.users?.hydratedAt || 0),
          },
          orgs: {
            ownerId: String(parsed.options?.orgs?.ownerId || parsed.user.id),
            items: Array.isArray(parsed.options?.orgs?.items)
              ? parsed.options.orgs.items.map((org) => normalizedOrg(org) as OrgListItem)
              : [],
            hydratedAt: Number(parsed.options?.orgs?.hydratedAt || 0),
          },
        },
        ...(pendingTenant
          ? {
              pendingTenant: {
                userId: String(pendingTenant.userId || ""),
                parentOrgId: String(pendingTenant.parentOrgId || ""),
                child: {
                  id: String(pendingTenant.child?.id || ""),
                  name: String(pendingTenant.child?.name || ""),
                },
              },
            }
          : {}),
      };
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error("Active context storage is invalid");
      }
      throw error;
    }
  }

  public selectedUserId(): string | null {
    return this.read()?.user.id || null;
  }

  public selectedOrgId(): string | null {
    return this.read()?.org.id || null;
  }

  private write(context: ActiveContext): ActiveContext {
    const next = {
      fingerprint: context.fingerprint,
      user: normalizedUser(context.user),
      org: normalizedOrg(context.org),
      options: {
        users: {
          ownerId: String(context.options.users.ownerId || ""),
          items: context.options.users.items.map(normalizedUser),
          hydratedAt: Number(context.options.users.hydratedAt || 0),
        },
        orgs: {
          ownerId: String(context.options.orgs.ownerId || context.user.id),
          items: context.options.orgs.items.map(
            (org) => normalizedOrg(org) as OrgListItem,
          ),
          hydratedAt: Number(context.options.orgs.hydratedAt || 0),
        },
      },
      ...(context.pendingTenant
        ? { pendingTenant: context.pendingTenant }
        : {}),
    };
    // ONE atomic slot replace inside the ONE session record — every selection
    // or hydration replaces the whole scoped snapshot.
    replaceSessionSlot("activeContext", next);
    return next;
  }

  /** Replace the complete authenticated scope in one localStorage write. */
  public hydrate(hydration: ActiveContextHydration): ActiveContext {
    const current = this.read();
    const user = normalizedUser(hydration.user);
    const org = normalizedOrg(hydration.org);
    if (
      current?.fingerprint === hydration.fingerprint &&
      current.user.id === user.id &&
      current.org.id === org.id
    ) {
      return current;
    }
    return this.write({
      fingerprint: hydration.fingerprint,
      user,
      org,
      options: {
        users: {
          ownerId: hydration.options.users.ownerId,
          items: hydration.options.users.items.map(normalizedUser),
          hydratedAt: hydration.options.users.hydratedAt,
        },
        orgs: {
          ownerId: hydration.options.orgs.ownerId,
          items: hydration.options.orgs.items.map(
            (item) => normalizedOrg(item) as OrgListItem,
          ),
          hydratedAt: hydration.options.orgs.hydratedAt,
        },
      },
      ...(current?.user.id === user.id && current.org.id === org.id && current.pendingTenant
        ? { pendingTenant: current.pendingTenant }
        : {}),
    });
  }

  public selectUser(user: ActiveUserSummary): ActiveContext {
    const orgId = String(user.activeOrgId || "").trim();
    if (!orgId) {
      throw new Error("Selected user has no active organization");
    }
    const current = this.read();
    return this.write({
      fingerprint: null,
      user,
      org: { id: orgId, name: orgId },
      options: {
        users: current?.options.users ?? this.emptyOptions<ActiveUserSummary>(""),
        orgs: this.emptyOptions<OrgListItem>(user.id),
      },
    });
  }

  public selectOrg(org: ActiveOrg): ActiveContext {
    const current = this.read();
    if (!current) throw new Error("Active user is unavailable");
    return this.write({ ...current, fingerprint: null, org: normalizedOrg(org) });
  }

  /** Persist the auth-door organization choice and every owned option scope once. */
  public selectInitialOrganization(
    selection: OrganizationSelection,
    orgId: string,
  ): ActiveContext {
    const user = normalizedUser(selection.user);
    const orgs = selection.options.orgs.items.map(
      (org) => normalizedOrg(org) as OrgListItem,
    );
    if (selection.options.orgs.ownerId !== user.id) {
      throw new Error("Organization choices belong to a different user");
    }
    const org = orgs.find((candidate) => candidate.id === orgId);
    if (!org) throw new Error("Selected organization is unavailable");
    const users = selection.options.users.items.map(normalizedUser);
    return this.write({
      fingerprint: null,
      user: normalizedUser({ ...user, activeOrgId: org.id }),
      org,
      options: {
        users: {
          ownerId: selection.options.users.ownerId,
          items: users,
          hydratedAt: selection.options.users.hydratedAt,
        },
        orgs: {
          ownerId: user.id,
          items: orgs,
          hydratedAt: selection.options.orgs.hydratedAt,
        },
      },
    });
  }

  public invalidateOrgOptions(): ActiveContext {
    const current = this.read();
    if (!current) throw new Error("Active context is unavailable");
    return this.write({
      ...current,
      fingerprint: null,
      options: {
        ...current.options,
        orgs: this.emptyOptions<OrgListItem>(current.user.id),
      },
    });
  }

  public queueTenantAttachment(
    child: Pick<ActiveOrg, "id" | "name">,
  ): ActiveContext {
    const current = this.read();
    if (!current) throw new Error("Active context is unavailable");
    const normalizedChild = normalizedOrg(child as ActiveOrg);
    if (normalizedChild.id === current.org.id) {
      throw new Error("Parent and child organizations must differ");
    }
    return this.write({
      fingerprint: null,
      user: current.user,
      org: current.org,
      options: {
        ...current.options,
        orgs: this.emptyOptions<OrgListItem>(current.user.id),
      },
      pendingTenant: {
        userId: current.user.id,
        parentOrgId: current.org.id,
        child: { id: normalizedChild.id, name: normalizedChild.name },
      },
    });
  }

  public async completePendingTenantAttachment(): Promise<OrgListItem | null> {
    const pending = this.read()?.pendingTenant;
    if (!pending) return null;
    if (!this.tenantAttachmentInFlight) {
      this.tenantAttachmentInFlight = (async () => {
        const selected = this.read();
        if (
          !selected?.pendingTenant ||
          selected.user.id !== pending.userId ||
          selected.org.id !== pending.parentOrgId
        ) {
          throw new Error("Pending tenant does not match the active context");
        }
        const { data } = await fetchClient.post<AttachTenantResponse>(
          "/org/attach",
          { childOrgId: pending.child.id },
        );
        if (
          data.userId !== pending.userId ||
          data.parentOrgId !== pending.parentOrgId ||
          data.tenant?.id !== pending.child.id
        ) {
          throw new Error("Tenant attachment returned a different scope");
        }
        const items = Array.isArray(data.items)
          ? data.items.map((org) => normalizedOrg(org) as OrgListItem)
          : [];
        const tenant = normalizedOrg(data.tenant) as OrgListItem;
        const current = this.read();
        if (
          !current?.pendingTenant ||
          current.user.id !== pending.userId ||
          current.org.id !== pending.parentOrgId ||
          current.pendingTenant.child.id !== pending.child.id
        ) {
          throw new Error("Active context changed during tenant attachment");
        }
        this.write({
          fingerprint: null,
          user: current.user,
          org: current.org,
          options: {
            ...current.options,
            orgs: {
              ownerId: current.user.id,
              items,
              hydratedAt: Date.now(),
            },
          },
        });
        return tenant;
      })().finally(() => {
        this.tenantAttachmentInFlight = null;
      });
    }
    return this.tenantAttachmentInFlight;
  }

  public clear(): void {
    replaceSessionSlot("activeContext", null);
    try {
      localStorage.removeItem(LEGACY_ACTIVE_CONTEXT_STORAGE_KEY);
    } catch {
      throw new Error("Active context storage is unavailable");
    }
  }

  public async users(adminId: string, force = false): Promise<ActiveUserSummary[]> {
    const context = this.read();
    const scoped = context?.options.users;
    const cached = scoped?.ownerId === adminId
      && Date.now() - scoped.hydratedAt < LIST_CACHE_MS;
    if (!force && cached) return scoped.items;
    if (!this.userListInFlight) {
      this.userListInFlight = fetchClient
        .post<ListResponse<ActiveUserSummary>>("/list", { actor: "human" })
        .then(({ data }) => {
          if (data.actor !== "human") {
            throw new Error("Active context store received a non-human list");
          }
          const items = Array.isArray(data.items) ? data.items : [];
          const current = this.read();
          if (current) {
            const activeUser = items.find((item) => item.id === current.user.id);
            this.write({
              fingerprint: null,
              user: activeUser
                ? normalizedUser({ ...current.user, ...activeUser })
                : current.user,
              org: current.org,
              options: {
                ...current.options,
                users: { ownerId: adminId, items, hydratedAt: Date.now() },
              },
              ...(current.pendingTenant
                ? { pendingTenant: current.pendingTenant }
                : {}),
            });
          }
          return items;
        })
        .finally(() => {
          this.userListInFlight = null;
        });
    }
    return this.userListInFlight;
  }

  public async orgs(userId: string, force = false): Promise<OrgListItem[]> {
    const context = this.read();
    const scoped = context?.options.orgs;
    const cached = scoped?.ownerId === userId
      && Date.now() - scoped.hydratedAt < LIST_CACHE_MS;
    if (!force && cached) return scoped.items;
    if (!this.orgListInFlight) {
      this.orgListInFlight = fetchClient
        .post<ListResponse<OrgListItem>>("/list", { actor: "org" })
        .then(({ data }) => {
          if (data.actor !== "org") {
            throw new Error("Active context store received a non-organization list");
          }
          const items = Array.isArray(data.items) ? data.items : [];
          const current = this.read();
          if (current?.user.id === userId) {
            const activeOrg = items.find((item) => item.id === current.org.id);
            this.write({
              fingerprint: null,
              user: current.user,
              org: activeOrg
                ? normalizedOrg({ ...current.org, ...activeOrg })
                : current.org,
              options: {
                ...current.options,
                orgs: { ownerId: userId, items, hydratedAt: Date.now() },
              },
              ...(current.pendingTenant
                ? { pendingTenant: current.pendingTenant }
                : {}),
            });
          }
          return items;
        })
        .finally(() => {
          this.orgListInFlight = null;
        });
    }
    return this.orgListInFlight;
  }

}

export const activeContextStore = new ActiveContextStore();
